import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faChartBar, faChartArea, faFilter, faDownload, faCalendarAlt, faExpand, faTimes, faInfoCircle, faBrain, faLightbulb, faExclamationTriangle, faArrowTrendUp, faSpinner, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faFacebook, faTwitter, faLinkedin, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { useAnalytics } from '../../context/AnalyticsContext';
import { useAuth } from '../../context/AuthContext';
import { analyticsAPI, creditsAPI, analysisJobsAPI, businessAuthAPI } from '../../services/api';
import PreparingMessage from '../ui/PreparingMessage';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BubbleController,
  BubbleDataPoint,
  Point,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BubbleController
);

const AnalyticsPage: React.FC = () => {
  // @ts-expect-error - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t, i18n } = useTranslation('dashboard');
  const { state, fetchEngagementData, fetchPlatformData, fetchBubbleData, fetchPlatformSpecificData, fetchAIAnalysis, fetchAllAnalyticsData } = useAnalytics();
  const { business, refreshBusinessProfile } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [modalChart, setModalChart] = useState<string | null>(null);
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  
  // Check analysis status from business model
  const analysisStatus = business?.analysisStatus?.['social-media-analytics']?.status || 'noanalyze';
  const isAnalysisPending = analysisStatus === 'pending';
  const isAnalysisCompleted = analysisStatus === 'completed';
  const isAnalysisFailed = analysisStatus === 'failed';
  const hasAnyAnalysis = isAnalysisCompleted;

  // Analysis start flow states
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Social media username setup states
  const [platforms, setPlatforms] = useState<Record<string, { username: string; loading: boolean; profile: any; error: string | null; verified: boolean; lastVerifiedUsername?: string | null }>>({
    facebook: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    instagram: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    twitter: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    tiktok: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    linkedin: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
  });
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  // Chart refs
  const engagementChartRef = useRef<HTMLCanvasElement>(null);
  const reachChartRef = useRef<HTMLCanvasElement>(null);
  const bubbleChartRef = useRef<HTMLCanvasElement>(null);
  const instagramChartRef = useRef<HTMLCanvasElement>(null);
  const facebookChartRef = useRef<HTMLCanvasElement>(null);
  const twitterChartRef = useRef<HTMLCanvasElement>(null);
  const linkedinChartRef = useRef<HTMLCanvasElement>(null);
  const tiktokChartRef = useRef<HTMLCanvasElement>(null);

  // Modal chart refs
  const modalEngagementChartRef = useRef<HTMLCanvasElement>(null);
  const modalBubbleChartRef = useRef<HTMLCanvasElement>(null);
  const modalInstagramChartRef = useRef<HTMLCanvasElement>(null);
  const modalFacebookChartRef = useRef<HTMLCanvasElement>(null);
  const modalTwitterChartRef = useRef<HTMLCanvasElement>(null);
  const modalLinkedinChartRef = useRef<HTMLCanvasElement>(null);
  const modalTiktokChartRef = useRef<HTMLCanvasElement>(null);

  // Chart instances
  const chartInstancesRef = useRef<{ [key: string]: ChartJS | null }>({});

  const formatNumber = (value: number | undefined) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '-';
    const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
    return value.toLocaleString(locale);
  };

  const bubbleTooltipCallbacks = {
    label: (context: any) => {
      const datasetLabel = context.dataset?.label ? `${context.dataset.label}` : '';
      const engagement = context.parsed?.x as number | undefined;
      const reach = context.parsed?.y as number | undefined;
      const rawData = context.raw as { rawR?: number; r?: number } | undefined;
      const followerCount = rawData?.rawR ?? rawData?.r;

      const lines = [] as string[];
      if (datasetLabel) lines.push(datasetLabel);
      if (engagement !== undefined) lines.push(`${t('socialMedia.common.engagement')}: ${formatNumber(engagement)}`);
      if (reach !== undefined) lines.push(`${t('socialMedia.common.reach')}: ${formatNumber(reach)}`);
      if (followerCount !== undefined) lines.push(`${t('socialMedia.common.followers')}: ${formatNumber(followerCount)}`);
      return lines;
    }
  };

  // Sample data
  const generateData = (days: number) => {
    const labels = [];
    const engagementData = [];
    const reachData = [];
    const impressionsData = [];

    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
      labels.push(date.toLocaleDateString(locale, { month: 'short', day: 'numeric' }));

      engagementData.push(Math.floor(Math.random() * 1000) + 500);
      reachData.push(Math.floor(Math.random() * 5000) + 2000);
      impressionsData.push(Math.floor(Math.random() * 10000) + 5000);
    }

    return { labels, engagementData, reachData, impressionsData };
  };


  // Check if user has any social media accounts
  const hasAnySocialMediaAccount = useMemo(() => {
    if (!business) return false;
    const platformHasData = (p?: { username?: string; isConnected?: boolean }) =>
      !!(p?.username && p.username.trim()) || p?.isConnected === true;
    return platformHasData(business.facebook) || 
           platformHasData(business.instagram) || 
           platformHasData(business.twitter) || 
           platformHasData(business.tiktok) || 
           platformHasData(business.linkedin);
  }, [business]);

  // Check sync status for all connected social media accounts
  const syncStatuses = useMemo(() => {
    if (!business) return { hasAnyPending: false, allCompleted: false, hasAnyAccount: false };
    
    const accounts = [
      business.facebook,
      business.instagram,
      business.twitter,
      business.tiktok,
      business.linkedin
    ];
    
    const connectedAccounts = accounts.filter(acc => 
      acc && (!!(acc.username && acc.username.trim()) || acc.isConnected === true)
    );
    
    if (connectedAccounts.length === 0) {
      return { hasAnyPending: false, allCompleted: false, hasAnyAccount: false };
    }
    
    const hasAnyPending = connectedAccounts.some(acc => acc && acc.syncStatus === 'pending');
    const allCompleted = connectedAccounts.every(acc => acc && acc.syncStatus === 'completed');
    
    return { hasAnyPending, allCompleted, hasAnyAccount: true };
  }, [business]);

  // Handle username change
  const handleUsernameChange = (platform: string, value: string) => {
    setPlatforms(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        username: value,
        profile: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].profile : null,
        error: null,
        verified: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].verified : false,
      }
    }));
    setSetupError(null);
  };

  // Handle verify all
  const handleVerifyAll = async () => {
    const payload: Record<string, string> = {};
    Object.entries(platforms).forEach(([platform, state]) => {
      const trimmed = state.username.trim();
      if (trimmed && trimmed !== state.lastVerifiedUsername) {
        payload[platform] = trimmed;
      }
    });

    if (Object.keys(payload).length === 0) {
      return;
    }

    setVerifying(true);
    setPlatforms(prev => {
      const next = { ...prev };
      Object.keys(payload).forEach((platform) => {
        next[platform] = {
          ...next[platform],
          loading: true,
          error: null,
        };
      });
      return next;
    });
    setSetupError(null);

    try {
      const response = await businessAuthAPI.getSocialMediaProfiles(payload);
      const respData = response?.data?.data || {};
      const respErrors = (response?.data as any)?.errors || {};

      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          const platformResp = (respData as any)[platform] || null;
          if (platformResp) {
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: platformResp,
              verified: true,
              error: null,
              lastVerifiedUsername: next[platform].username.trim(),
            };
          } else {
            const errorMsg = (respErrors as any)?.[platform] || t('socialMedia.analytics.profileNotFound');
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: null,
              verified: false,
              error: errorMsg,
            };
          }
        });
        return next;
      });
    } catch (err: any) {
      const genericError = err?.response?.data?.message || t('socialMedia.analytics.verificationFailed');
      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          next[platform] = {
            ...next[platform],
            loading: false,
            profile: null,
            verified: false,
            error: err?.response?.data?.errors?.[platform] || genericError,
          };
        });
        return next;
      });
    } finally {
      setVerifying(false);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    if (!hasAnyUsername) {
      setSetupError(t('socialMedia.analytics.enterAtLeastOneUsername'));
      return;
    }

    const hasAnyVerified = Object.values(platforms).some(p => p.verified);
    if (!hasAnyVerified) {
      setSetupError(t('socialMedia.analytics.verifyAtLeastOneAccount'));
      return;
    }

    setSubmitting(true);
    setSetupError(null);

    try {
      const payload: any = {};
      Object.entries(platforms).forEach(([platform, state]) => {
        if (state.verified && state.username.trim()) {
          payload[platform] = state.username.trim();
        }
      });

      await businessAuthAPI.addSocialMediaAccounts(payload);
      await refreshBusinessProfile();
      
      try {
        void businessAuthAPI.syncSocialMediaData();
      } catch (_e) {
        // Ignore sync errors
      }
    } catch (err: any) {
      setSetupError(err.response?.data?.message || t('socialMedia.analytics.accountSaveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const getProxyImageUrl = (url?: string) => {
    if (!url) return '';
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}`;
  };

  const platformConfig = {
    facebook: { icon: faFacebook, label: t('socialMedia.analytics.platforms.facebook'), placeholder: t('socialMedia.analytics.platformPlaceholders.facebook') },
    instagram: { icon: faInstagram, label: t('socialMedia.analytics.platforms.instagram'), placeholder: t('socialMedia.analytics.platformPlaceholders.instagram') },
    twitter: { icon: faTwitter, label: t('socialMedia.analytics.platforms.twitter'), placeholder: t('socialMedia.analytics.platformPlaceholders.twitter') },
    tiktok: { icon: faTiktok, label: t('socialMedia.analytics.platforms.tiktok'), placeholder: t('socialMedia.analytics.platformPlaceholders.tiktok') },
    linkedin: { icon: faLinkedin, label: t('socialMedia.analytics.platforms.linkedin'), placeholder: t('socialMedia.analytics.platformPlaceholders.linkedin') },
  };

  // Component mount olduğunda verileri yükle - only if all syncs completed and analysis exists
  useEffect(() => {
    if (syncStatuses.allCompleted && isAnalysisCompleted && business?._id) {
      // Tüm analytics verisini tek seferde çek
      fetchAllAnalyticsData();
    }
  }, [syncStatuses.allCompleted, isAnalysisCompleted, business?._id, fetchAllAnalyticsData]);

  // AI Analysis data
  const aiAnalyses = {
    engagement: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.common.engagementTrend'),
          message: t('socialMedia.analytics.aiInsights.engagement.engagementIncrease'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.analytics.aiInsights.engagement.futureWeekPrediction'),
          message: t('socialMedia.analytics.aiInsights.engagement.futureWeekMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        },
        {
          type: 'warning',
          title: t('socialMedia.analytics.aiInsights.engagement.attentionNeeded'),
          message: t('socialMedia.analytics.aiInsights.engagement.attentionMessage'),
          icon: faExclamationTriangle,
          color: 'text-yellow-400'
        }
      ]
    },
    platform: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.analytics.aiInsights.platform.performance'),
          message: t('socialMedia.analytics.aiInsights.platform.performanceMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.analytics.aiInsights.platform.recommendation'),
          message: t('socialMedia.analytics.aiInsights.platform.recommendationMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        },
        {
          type: 'warning',
          title: t('socialMedia.analytics.aiInsights.platform.twitterPerformance'),
          message: t('socialMedia.analytics.aiInsights.platform.twitterPerformanceMessage'),
          icon: faExclamationTriangle,
          color: 'text-yellow-400'
        }
      ]
    },
    bubble: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.common.engagementReachRelationship'),
          message: t('socialMedia.analytics.aiInsights.bubble.instagramReachMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.analytics.aiInsights.bubble.optimizationOpportunity'),
          message: t('socialMedia.analytics.aiInsights.bubble.optimizationMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        }
      ]
    },
    instagram: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.analytics.aiInsights.instagram.success'),
          message: t('socialMedia.analytics.aiInsights.instagram.successMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.analytics.aiInsights.instagram.growthPotential'),
          message: t('socialMedia.analytics.aiInsights.instagram.growthMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        }
      ]
    },
    facebook: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.analytics.aiInsights.facebook.stability'),
          message: t('socialMedia.analytics.aiInsights.facebook.stabilityMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'warning',
          title: t('socialMedia.analytics.aiInsights.facebook.contentDiversity'),
          message: t('socialMedia.analytics.aiInsights.facebook.contentDiversityMessage'),
          icon: faExclamationTriangle,
          color: 'text-yellow-400'
        }
      ]
    },
    twitter: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.analytics.aiInsights.twitter.activity'),
          message: t('socialMedia.analytics.aiInsights.twitter.activityMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.analytics.aiInsights.twitter.hashtagStrategy'),
          message: t('socialMedia.analytics.aiInsights.twitter.hashtagMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        }
      ]
    },
    linkedin: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.analytics.aiInsights.linkedin.professionalism'),
          message: t('socialMedia.analytics.aiInsights.linkedin.professionalismMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.analytics.aiInsights.linkedin.networkingOpportunity'),
          message: t('socialMedia.analytics.aiInsights.linkedin.networkingMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        }
      ]
    },
    tiktok: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.analytics.aiInsights.tiktok.viralPotential'),
          message: t('socialMedia.analytics.aiInsights.tiktok.viralMessage'),
          icon: faLightbulb,
          color: 'text-pink-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.analytics.aiInsights.tiktok.trendFollowing'),
          message: t('socialMedia.analytics.aiInsights.tiktok.trendMessage'),
          icon: faArrowTrendUp,
          color: 'text-red-400'
        }
      ]
    }
  };

  // Memoized tooltip handlers to prevent chart re-renders
  const handleTooltipEnter = useCallback((tooltipId: string) => {
    setHoveredTooltip(tooltipId);
  }, []);

  const handleTooltipLeave = useCallback(() => {
    setHoveredTooltip(null);
  }, []);

  // Memoize chart data to prevent unnecessary re-renders
  // Chart data memoization - context'ten gelen verileri kullan
  const chartData = useMemo(() => {
    if (!state.engagement) return null;
    return {
      labels: state.engagement.labels,
      engagementData: state.engagement.datasets[0]?.data as number[] || [],
      reachData: state.engagement.datasets[1]?.data as number[] || [],
      impressionsData: state.engagement.datasets[2]?.data as number[] || []
    };
  }, [state.engagement]);

  // Simple Tooltip component
  const Tooltip: React.FC<{ id: string; content: string; children: React.ReactNode }> = ({ id, content, children }) => (
    <div className="relative inline-block">
      {children}
      {hoveredTooltip === id && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 w-64 text-left">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );

  // AI Analysis component
  const AIAnalysis: React.FC<{ chartType: string }> = ({ chartType }) => {
    const analysis = state.aiAnalysis[chartType];
    const isLoading = state.loading.aiAnalysis[chartType];
    const error = state.error.aiAnalysis[chartType];
    
    if (isLoading) {
      return (
        <div className="mt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-white/70">{t('socialMedia.analytics.aiAnalysisLoading')}</div>
          </div>
        </div>
      );
    }
    
    if (error === 'PREPARING') {
      return (
        <div className="mt-6">
          <PreparingMessage type="ai" message={t('socialMedia.analytics.aiInsightsPreparing')} />
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="mt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-red-400">{t('socialMedia.analytics.aiAnalysisError')} {error}</div>
          </div>
        </div>
      );
    }
    
    if (!analysis) return null;

    return (
      <div className="mt-6 space-y-3">
        <div className="flex items-center space-x-2 mb-4">
          <FontAwesomeIcon icon={faBrain} className="text-purple-400 text-lg" />
          <h4 className="text-lg font-semibold text-white">{t('socialMedia.common.aiAnalysisTitle')}</h4>
        </div>
        <div className="space-y-3">
          {analysis.insights.map((insight, index) => (
            <div key={index} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  insight.type === 'insight' ? 'bg-blue-500/20' :
                  insight.type === 'prediction' ? 'bg-green-500/20' :
                  insight.type === 'recommendation' ? 'bg-purple-500/20' :
                  insight.type === 'warning' ? 'bg-yellow-500/20' :
                  'bg-gray-500/20'
                }`}>
                  <FontAwesomeIcon
                    icon={
                      insight.type === 'insight' ? faLightbulb :
                      insight.type === 'prediction' ? faArrowTrendUp :
                      insight.type === 'recommendation' ? faBrain :
                      insight.type === 'warning' ? faExclamationTriangle :
                      faInfoCircle
                    }
                    className={`text-sm ${
                      insight.type === 'insight' ? 'text-blue-400' :
                      insight.type === 'prediction' ? 'text-green-400' :
                      insight.type === 'recommendation' ? 'text-purple-400' :
                      insight.type === 'warning' ? 'text-yellow-400' :
                      'text-gray-400'
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <h5 className="text-white font-medium mb-1">{insight.title}</h5>
                  <p className="text-white/70 text-sm leading-relaxed">{insight.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Modal chart creation functions
  const createModalChart = (chartType: string, ref: React.RefObject<HTMLCanvasElement | null>) => {
    if (!ref.current) return;

    const ctx = ref.current.getContext('2d');
    if (!ctx) return;

    const modalKey = `modal_${chartType}`;
    if (chartInstancesRef.current[modalKey]) {
      chartInstancesRef.current[modalKey]?.destroy();
    }

    let chart: ChartJS;

    switch (chartType) {
      case 'engagement':
        chart = new ChartJS(ctx, {
          type: 'line',
          data: {
            labels: state.engagement?.labels || [],
            datasets: [
              {
                label: t('socialMedia.common.engagement'),
                data: state.engagement?.datasets[0]?.data as number[] || [],
                borderColor: 'rgb(168, 85, 247)',
                backgroundColor: 'rgba(168, 85, 247, 0.1)',
                borderWidth: 3,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: 'rgb(168, 85, 247)',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 6,
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: { size: 14 }
                }
              }
            },
            scales: {
              x: {
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                ticks: { color: 'rgba(255, 255, 255, 0.6)', font: { size: 12 } }
              },
              y: {
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                ticks: { color: 'rgba(255, 255, 255, 0.6)', font: { size: 12 } }
              }
            }
          }
        });
        break;

      case 'bubble':
        if (!state.bubble) return;
        
        // Platform renkleri
        const platformColors: { [key: string]: { bg: string; border: string } } = {
          Instagram: { bg: 'rgba(225, 48, 108, 0.6)', border: 'rgb(225, 48, 108)' },
          Facebook: { bg: 'rgba(24, 119, 242, 0.6)', border: 'rgb(24, 119, 242)' },
          Twitter: { bg: 'rgba(29, 161, 242, 0.6)', border: 'rgb(29, 161, 242)' },
          Linkedin: { bg: 'rgba(0, 119, 181, 0.6)', border: 'rgb(0, 119, 181)' },
          Tiktok: { bg: 'rgba(255, 0, 80, 0.6)', border: 'rgb(255, 0, 80)' }
        };

        // State'ten gelen datasets'leri platform renkleriyle zenginleştir
        const bubbleDatasets = state.bubble.datasets.map((dataset: any) => {
          const platformName = dataset.label;
          const colors = platformColors[platformName] || { bg: 'rgba(168, 85, 247, 0.6)', border: 'rgb(168, 85, 247)' };
          return {
            ...dataset,
            backgroundColor: colors.bg,
            borderColor: colors.border,
            borderWidth: 2,
          };
        });

        chart = new ChartJS(ctx, {
          type: 'bubble',
          data: {
            datasets: bubbleDatasets
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: { size: 14 }
                }
              },
              tooltip: {
                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                titleColor: 'rgba(255, 255, 255, 0.9)',
                bodyColor: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                callbacks: bubbleTooltipCallbacks
              }
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: t('socialMedia.common.engagement'),
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: { size: 14 }
                },
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                ticks: { color: 'rgba(255, 255, 255, 0.6)', font: { size: 12 } }
              },
              y: {
                title: {
                  display: true,
                  text: t('socialMedia.common.reach'),
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: { size: 14 }
                },
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                ticks: { color: 'rgba(255, 255, 255, 0.6)', font: { size: 12 } }
              }
            }
          }
        });
        break;

      default:
        // Platform specific charts
        const color = chartType === 'instagram' ? 'rgb(225, 48, 108)' :
          chartType === 'facebook' ? 'rgb(24, 119, 242)' :
            chartType === 'twitter' ? 'rgb(29, 161, 242)' :
              chartType === 'linkedin' ? 'rgb(0, 119, 181)' :
                'rgb(255, 0, 80)';

        chart = new ChartJS(ctx, {
          type: 'line',
          data: {
            labels: state.engagement?.labels || [],
            datasets: [
              {
                label: t('socialMedia.common.followerGrowth'),
                data: (state.engagement?.datasets[0]?.data as number[] || []).map((val: number) => val * (0.7 + Math.random() * 0.6)),
                borderColor: color,
                backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
                borderWidth: 2,
                fill: true,
                tension: 0.4,
              },
              {
                label: t('socialMedia.common.engagement'),
                data: (state.engagement?.datasets[0]?.data as number[] || []).map((val: number) => val * (0.5 + Math.random() * 0.8)),
                borderColor: color.replace('rgb', 'rgba').replace(')', ', 0.7)'),
                backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.05)'),
                borderWidth: 2,
                fill: false,
                tension: 0.4,
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: { size: 14 }
                }
              }
            },
            scales: {
              x: {
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                ticks: { color: 'rgba(255, 255, 255, 0.6)', font: { size: 12 } }
              },
              y: {
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                ticks: { color: 'rgba(255, 255, 255, 0.6)', font: { size: 12 } }
              }
            }
          }
        });
    }

    chartInstancesRef.current[modalKey] = chart;
  };

  // Engagement over time chart
  useEffect(() => {
    if (engagementChartRef.current && state.engagement) {
      const ctx = engagementChartRef.current.getContext('2d');
      if (ctx) {
        // Destroy existing chart
        if (chartInstancesRef.current.engagement) {
          chartInstancesRef.current.engagement.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'line',
          data: state.engagement,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.engagement = chart;
      }
    }
  }, [state.engagement]); // Context'ten gelen veri değiştiğinde render et

  // Bubble chart for engagement vs reach
  useEffect(() => {
    if (bubbleChartRef.current && state.bubble) {
      const ctx = bubbleChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.bubble) {
          chartInstancesRef.current.bubble.destroy();
        }

        // Platform renkleri
        const platformColors: { [key: string]: { bg: string; border: string } } = {
          Instagram: { bg: 'rgba(225, 48, 108, 0.6)', border: 'rgb(225, 48, 108)' },
          Facebook: { bg: 'rgba(24, 119, 242, 0.6)', border: 'rgb(24, 119, 242)' },
          Twitter: { bg: 'rgba(29, 161, 242, 0.6)', border: 'rgb(29, 161, 242)' },
          Linkedin: { bg: 'rgba(0, 119, 181, 0.6)', border: 'rgb(0, 119, 181)' },
          Tiktok: { bg: 'rgba(255, 0, 80, 0.6)', border: 'rgb(255, 0, 80)' }
        };

        // State'ten gelen datasets'leri platform renkleriyle zenginleştir
        const datasets = state.bubble.datasets.map((dataset: any) => {
          const platformName = dataset.label;
          const colors = platformColors[platformName] || { bg: 'rgba(168, 85, 247, 0.6)', border: 'rgb(168, 85, 247)' };
          return {
            ...dataset,
            backgroundColor: colors.bg,
            borderColor: colors.border,
            borderWidth: 2,
          };
        });

        const chart = new ChartJS(ctx, {
          type: 'bubble',
          data: {
            datasets
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              },
              tooltip: {
                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                titleColor: 'rgba(255, 255, 255, 0.9)',
                bodyColor: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                callbacks: bubbleTooltipCallbacks
              }
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: t('socialMedia.common.engagement'),
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                title: {
                  display: true,
                  text: t('socialMedia.common.reach'),
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.bubble = chart;
      }
    }
  }, [state.bubble]);

  // Platform specific charts
  const createPlatformChart = (ref: React.RefObject<HTMLCanvasElement | null>, platform: string, color: string) => {
    if (ref.current) {
      const ctx = ref.current.getContext('2d');
      if (ctx) {
        const chartKey = platform.toLowerCase();
        if (chartInstancesRef.current[chartKey]) {
          chartInstancesRef.current[chartKey]?.destroy();
        }

        const platformData = state.platformSpecific[platform as keyof typeof state.platformSpecific];
        if (!platformData) return;

        const chart = new ChartJS(ctx, {
          type: 'line',
          data: platformData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 11
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: {
                    size: 10
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.6)',
                  font: {
                    size: 10
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current[chartKey] = chart;
      }
    }
  };

  useEffect(() => {
    createPlatformChart(instagramChartRef, 'instagram', 'rgb(225, 48, 108)');
    createPlatformChart(facebookChartRef, 'facebook', 'rgb(24, 119, 242)');
    createPlatformChart(twitterChartRef, 'twitter', 'rgb(29, 161, 242)');
    createPlatformChart(linkedinChartRef, 'linkedin', 'rgb(0, 119, 181)');
    createPlatformChart(tiktokChartRef, 'tiktok', 'rgb(255, 0, 80)');
  }, [selectedPeriod, state.platformSpecific]); // ✅ Hem selectedPeriod hem de platformSpecific değişikliklerini dinle

  // Cleanup charts on unmount
  useEffect(() => {
    return () => {
      Object.values(chartInstancesRef.current).forEach(chart => {
        if (chart) chart.destroy();
      });
    };
  }, []);

  // Show social media username setup if no accounts exist
  if (!hasAnySocialMediaAccount) {
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    const allEnteredAreVerified = Object.values(platforms).every(p => !p.username.trim() || p.verified);
    const primaryCtaIsContinue = hasAnyUsername && allEnteredAreVerified && !verifying;

    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-2xl space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
              <FontAwesomeIcon icon={faChartLine} className="text-3xl sm:text-4xl text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('socialMedia.common.connectAllAccounts')}</h1>
            <p className="text-white/70 text-sm sm:text-base mb-8">
              {t('socialMedia.common.connectAllDescription')}
            </p>
          </div>

          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-6">
            {setupError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {setupError}
              </div>
            )}

            <div className="space-y-4">
              {Object.entries(platformConfig).map(([platform, config]) => (
                <div key={platform} className="space-y-2">
                  <label className="flex items-center space-x-2 text-white/80 text-sm">
                    <FontAwesomeIcon icon={config.icon} className="text-lg" />
                    <span>{config.label}</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder={config.placeholder}
                      value={platforms[platform].username}
                      onChange={(e) => handleUsernameChange(platform, e.target.value)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                    />
                    {platforms[platform].verified && (
                      <FontAwesomeIcon icon={faCheck} className="text-green-400" />
                    )}
                    {platforms[platform].loading && (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-white/50" />
                    )}
                  </div>
                  {platforms[platform].error && (
                    <p className="text-red-400 text-xs">{platforms[platform].error}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleVerifyAll}
                disabled={verifying || !hasAnyUsername}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <span className="flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('socialMedia.analytics.verifying')}
                  </span>
                ) : (
                  t('socialMedia.analytics.verifyAccounts')
                )}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!primaryCtaIsContinue || submitting}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg text-white font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('socialMedia.analytics.adding')}
                  </span>
                ) : (
                  t('socialMedia.analytics.addAccounts')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show sync pending screen if any account is syncing
  if (syncStatuses.hasAnyAccount && syncStatuses.hasAnyPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faChartLine} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.analytics.dataSyncing')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            {t('socialMedia.analytics.dataSyncingDescription')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.analytics.syncCompleteMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show analysis start screen if all syncs completed but no analysis exists
  if (syncStatuses.allCompleted && !isAnalysisCompleted && !isAnalysisPending) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
                <FontAwesomeIcon icon={faBrain} className="text-3xl sm:text-4xl text-white" />
              </div>
              <FontAwesomeIcon icon={faChartLine} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.common.startAiAnalysis')}</h1>
              <p className="text-white/70 text-sm sm:text-base mb-6">
                {t('socialMedia.analytics.analyticsReady')}
              </p>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faLightbulb} className="text-yellow-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.common.aiAnalysisFeatures')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.detailedPerformance')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faChartLine} className="text-blue-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.common.chartsAndReports')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.chartsAndReportsDescription')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faBrain} className="text-purple-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.common.smartRecommendations')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.smartRecommendationsDescription')}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={async () => {
                  setAiModalOpen(true);
                  setEstimatedCredits(null);
                  // Set default dates (last 30 days)
                  const today = new Date();
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  setEndDate(today.toISOString().split('T')[0]);
                  setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                }}
                className="w-full bg-gradient-to-r from-purple-500 to-blue-600 px-6 py-4 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity text-base sm:text-lg"
              >
                <FontAwesomeIcon icon={faBrain} className="mr-2" />
                {t('socialMedia.common.startAiAnalysis')}
              </button>
            </div>
          </div>
        </div>

        {/* AI Analysis Modal */}
        {aiModalOpen && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
            <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-white/10">
                <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.common.aiAnalysisTitle')}</h3>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.startDate') || 'Başlangıç Tarihi'}</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.endDate') || 'Bitiş Tarihi'}</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                </div>
                <div className="text-white/80">
                  {t('socialMedia.common.estimatedCreditsLabel')}
                  <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                    {estimateLoading ? (
                      <span className="flex items-center">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        {t('socialMedia.common.calculating')}
                      </span>
                    ) : (
                      estimatedCredits !== null 
                        ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                        : '—'
                    )}
                  </div>
                </div>
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!startDate || !endDate}
                  onClick={async () => {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    setEstimatedCredits(null);
                    try {
                      const res = await creditsAPI.estimate('socialmediaanalytics', startDate, endDate);
                      const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                      setEstimatedCredits(typeof value === 'number' ? value : null);
                    } finally {
                      setEstimateLoading(false);
                    }
                  }}
                >
                  {t('socialMedia.common.calculateCredits') || 'Kredi Hesapla'}
                </button>
                <button
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                  onClick={async () => {
                    try {
                      if (!startDate || !endDate) return;
                      setEstimateLoading(true);
                      await analysisJobsAPI.start('socialmediaanalytics', startDate, endDate);
                    } finally {
                      setEstimateLoading(false);
                      setAiModalOpen(false);
                    }
                  }}
                >
                  {t('socialMedia.analytics.spendCredits')}
                </button>
                <button
                  className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                  onClick={() => {
                    setAiModalOpen(false);
                    setStartDate('');
                    setEndDate('');
                    setEstimatedCredits(null);
                  }}
                >
                  {t('socialMedia.analytics.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Show loading while analysis is pending (only if all syncs are completed)
  if (syncStatuses.allCompleted && isAnalysisPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faChartLine} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.analytics.analysisInProgress')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            {t('socialMedia.analytics.analysisInProgressDescription')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.analytics.analysisCompleteMessage')}
            </p>
          </div>
              </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{t('socialMedia.analytics.title')}</h1>
            <p className="text-white/70 text-sm md:text-base">{t('socialMedia.analytics.subtitle')}</p>
          </div>
          <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-2 md:space-y-0 md:space-x-4 w-full md:w-auto">
            <button
              onClick={async () => {
                setAiModalOpen(true);
                setEstimatedCredits(null);
                // Set default dates (last 30 days)
                const today = new Date();
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(today.getDate() - 30);
                setEndDate(today.toISOString().split('T')[0]);
                setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
              }}
              className="px-3 md:px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-xs md:text-sm md:hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="hidden sm:inline">{t('socialMedia.common.startAiAnalysis')}</span>
              <span className="sm:hidden">{t('socialMedia.common.aiAnalysis')}</span>
            </button>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-400 w-full md:w-auto"
            >
              <option value="7">{t('socialMedia.analytics.last7Days')}</option>
              <option value="30">{t('socialMedia.analytics.last30Days')}</option>
              <option value="90">{t('socialMedia.analytics.last90Days')}</option>
            </select>
            <button className="bg-gradient-to-r from-purple-500 to-purple-700 px-4 md:px-6 py-2 rounded-xl text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2 w-full md:w-auto">
              <FontAwesomeIcon icon={faDownload} />
              <span className="hidden md:inline">{t('socialMedia.analytics.downloadReport')}</span>
            </button>
          </div>
        </div>

        {/* General Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
          {/* Engagement Over Time */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-xl font-semibold text-white">{t('socialMedia.analytics.engagementOverTime')}</h3>
                <Tooltip
                  id="engagement-tooltip"
                  content={t('socialMedia.analytics.engagementTooltip')}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                    onMouseEnter={() => handleTooltipEnter('engagement-tooltip')}
                    onMouseLeave={handleTooltipLeave}
                  />
                </Tooltip>
              </div>
              <div className="flex items-center space-x-3">
                <FontAwesomeIcon icon={faChartLine} className="text-purple-400 text-lg" />
                <button
                  onClick={() => {
                    setModalChart('engagement');
                    setTimeout(() => createModalChart('engagement', modalEngagementChartRef), 100);
                  }}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                </button>
              </div>
            </div>
            <div className="h-80">
              {state.loading.engagement ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-white/70">{t('socialMedia.common.loading')}</div>
                </div>
              ) : state.error.engagement === 'PREPARING' ? (
                <PreparingMessage type="analytics" />
              ) : state.error.engagement ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-red-400">{t('socialMedia.common.error')}: {state.error.engagement}</div>
                </div>
              ) : (
                <canvas ref={engagementChartRef}></canvas>
              )}
            </div>
            <AIAnalysis chartType="engagement" />
          </div>

        </div>

        {/* Engagement vs Reach Bubble Chart */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <h3 className="text-xl font-semibold text-white">{t('socialMedia.analytics.engagementVsReach')}</h3>
              <Tooltip
                id="bubble-tooltip"
                content={t('socialMedia.analytics.bubbleTooltip')}
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                  onMouseEnter={() => handleTooltipEnter('bubble-tooltip')}
                  onMouseLeave={handleTooltipLeave}
                />
              </Tooltip>
            </div>
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon={faChartArea} className="text-green-400 text-lg" />
              <button
                onClick={() => {
                  setModalChart('bubble');
                  setTimeout(() => createModalChart('bubble', modalBubbleChartRef), 100);
                }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
              </button>
            </div>
          </div>
          <div className="h-96">
          {state.loading.bubble ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-white/70">{t('socialMedia.common.loading')}</div>
            </div>
          ) : state.error.bubble === 'PREPARING' ? (
            <PreparingMessage type="analytics" />
          ) : state.error.bubble ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-400">{t('socialMedia.common.error')}: {state.error.bubble}</div>
            </div>
          ) : (
            <canvas ref={bubbleChartRef}></canvas>
          )}
          </div>
          <AIAnalysis chartType="bubble" />
        </div>

        {/* Platform Specific Analytics */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6">{t('socialMedia.analytics.platformAnalytics')}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Instagram */}
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faInstagram} className="text-pink-500 text-2xl" />
                  <div className="flex items-center space-x-2">
                    <h3 className="text-xl font-semibold text-white">{t('socialMedia.analytics.platforms.instagram')}</h3>
                    <Tooltip
                      id="instagram-tooltip"
                      content={t('socialMedia.analytics.instagramTooltip')}
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                        onMouseEnter={() => handleTooltipEnter('instagram-tooltip')}
                        onMouseLeave={handleTooltipLeave}
                      />
                    </Tooltip>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setModalChart('instagram');
                    setTimeout(() => createModalChart('instagram', modalInstagramChartRef), 100);
                  }}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                </button>
              </div>
              <div className="h-64">
                <canvas ref={instagramChartRef}></canvas>
              </div>
              <AIAnalysis chartType="instagram" />
            </div>

            {/* Facebook */}
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faFacebook} className="text-blue-500 text-2xl" />
                  <div className="flex items-center space-x-2">
                    <h3 className="text-xl font-semibold text-white">{t('socialMedia.analytics.platforms.facebook')}</h3>
                    <Tooltip
                      id="facebook-tooltip"
                      content={t('socialMedia.analytics.facebookAnalyticsTooltip')}
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                        onMouseEnter={() => handleTooltipEnter('facebook-tooltip')}
                        onMouseLeave={handleTooltipLeave}
                      />
                    </Tooltip>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setModalChart('facebook');
                    setTimeout(() => createModalChart('facebook', modalFacebookChartRef), 100);
                  }}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                </button>
              </div>
              <div className="h-64">
                <canvas ref={facebookChartRef}></canvas>
              </div>
              <AIAnalysis chartType="facebook" />
            </div>

            {/* Twitter */}
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faTwitter} className="text-sky-500 text-2xl" />
                  <div className="flex items-center space-x-2">
                    <h3 className="text-xl font-semibold text-white">{t('socialMedia.analytics.platforms.twitter')}</h3>
                    <Tooltip
                      id="twitter-tooltip"
                      content={t('socialMedia.analytics.twitterTooltip')}
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                        onMouseEnter={() => handleTooltipEnter('twitter-tooltip')}
                        onMouseLeave={handleTooltipLeave}
                      />
                    </Tooltip>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setModalChart('twitter');
                    setTimeout(() => createModalChart('twitter', modalTwitterChartRef), 100);
                  }}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                </button>
              </div>
              <div className="h-64">
                <canvas ref={twitterChartRef}></canvas>
              </div>
              <AIAnalysis chartType="twitter" />
            </div>

            {/* LinkedIn */}
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faLinkedin} className="text-blue-600 text-2xl" />
                  <div className="flex items-center space-x-2">
                    <h3 className="text-xl font-semibold text-white">{t('socialMedia.analytics.platforms.linkedin')}</h3>
                    <Tooltip
                      id="linkedin-tooltip"
                      content={t('socialMedia.analytics.linkedinTooltip')}
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                        onMouseEnter={() => handleTooltipEnter('linkedin-tooltip')}
                        onMouseLeave={handleTooltipLeave}
                      />
                    </Tooltip>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setModalChart('linkedin');
                    setTimeout(() => createModalChart('linkedin', modalLinkedinChartRef), 100);
                  }}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                </button>
              </div>
              <div className="h-64">
                <canvas ref={linkedinChartRef}></canvas>
              </div>
              <AIAnalysis chartType="linkedin" />
            </div>

            {/* TikTok */}
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-pink-500 to-red-500 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">T</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-xl font-semibold text-white">{t('socialMedia.analytics.platforms.tiktok')}</h3>
                    <Tooltip
                      id="tiktok-tooltip"
                      content={t('socialMedia.analytics.tiktokTooltip')}
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                        onMouseEnter={() => handleTooltipEnter('tiktok-tooltip')}
                        onMouseLeave={handleTooltipLeave}
                      />
                    </Tooltip>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setModalChart('tiktok');
                    setTimeout(() => createModalChart('tiktok', modalTiktokChartRef), 100);
                  }}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
                </button>
              </div>
              <div className="h-64">
                <canvas ref={tiktokChartRef}></canvas>
              </div>
              <AIAnalysis chartType="tiktok" />
            </div>
          </div>
        </div>
      </div>
      {/* Modal */}
      {modalChart && (
        <div className="fixed inset-0 z-50">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setModalChart(null)}
          ></div>

          {/* Modal Content */}
          <div className="absolute top-4 left-4 right-4 bottom-4 md:top-10 md:left-10 md:right-10 md:bottom-10 backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h3 className="text-lg md:text-2xl font-bold text-white pr-2">
                {modalChart === 'engagement' && t('socialMedia.analytics.engagementOverTime')}
                {modalChart === 'bubble' && t('socialMedia.analytics.engagementVsReach')}
                {modalChart === 'instagram' && t('socialMedia.analytics.platforms.instagram') + ' ' + t('socialMedia.analytics.title')}
                {modalChart === 'facebook' && t('socialMedia.analytics.platforms.facebook') + ' ' + t('socialMedia.analytics.title')}
                {modalChart === 'twitter' && t('socialMedia.analytics.platforms.twitter') + ' ' + t('socialMedia.analytics.title')}
                {modalChart === 'linkedin' && t('socialMedia.analytics.platforms.linkedin') + ' ' + t('socialMedia.analytics.title')}
                {modalChart === 'tiktok' && t('socialMedia.analytics.platforms.tiktok') + ' ' + t('socialMedia.analytics.title')}
              </h3>
              <button
                onClick={() => setModalChart(null)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
              >
                <FontAwesomeIcon icon={faTimes} className="text-white/70 hover:text-white text-lg" />
              </button>
            </div>

            {/* Modal Chart */}
            <div className="h-[calc(100%-60px)] md:h-[calc(100%-80px)]">
              {modalChart === 'engagement' && <canvas ref={modalEngagementChartRef}></canvas>}
              {modalChart === 'bubble' && <canvas ref={modalBubbleChartRef}></canvas>}
              {modalChart === 'instagram' && <canvas ref={modalInstagramChartRef}></canvas>}
              {modalChart === 'facebook' && <canvas ref={modalFacebookChartRef}></canvas>}
              {modalChart === 'twitter' && <canvas ref={modalTwitterChartRef}></canvas>}
              {modalChart === 'linkedin' && <canvas ref={modalLinkedinChartRef}></canvas>}
              {modalChart === 'tiktok' && <canvas ref={modalTiktokChartRef}></canvas>}
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Modal */}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
          <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-white/10">
              <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.common.aiAnalysisTitle')}</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.startDate') || 'Başlangıç Tarihi'}</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.common.endDate') || 'Bitiş Tarihi'}</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
              </div>
              <div className="text-white/80">
                {t('socialMedia.common.estimatedCreditsLabel')}
                <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                  {estimateLoading ? (
                    <span className="flex items-center">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      {t('socialMedia.common.calculating')}
                    </span>
                  ) : (
                    estimatedCredits !== null 
                      ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                      : '—'
                  )}
                </div>
              </div>
              <button
                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!startDate || !endDate}
                onClick={async () => {
                  if (!startDate || !endDate) return;
                  setEstimateLoading(true);
                  setEstimatedCredits(null);
                  try {
                    const res = await creditsAPI.estimate('socialmediaanalytics', startDate, endDate);
                    const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                    setEstimatedCredits(typeof value === 'number' ? value : null);
                  } finally {
                    setEstimateLoading(false);
                  }
                }}
              >
                {t('socialMedia.common.calculateCredits') || 'Kredi Hesapla'}
              </button>
              <button
                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                onClick={async () => {
                  try {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    await analysisJobsAPI.start('socialmediaanalytics', startDate, endDate);
                  } finally {
                    setEstimateLoading(false);
                    setAiModalOpen(false);
                  }
                }}
              >
                {t('socialMedia.analytics.spendCredits')}
              </button>
              <button
                className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                onClick={() => {
                  setAiModalOpen(false);
                  setStartDate('');
                  setEndDate('');
                  setEstimatedCredits(null);
                }}
              >
                {t('socialMedia.analytics.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnalyticsPage;
